<template>
  <svg :height="height" :width="width">
    <polygon
      v-for="by in bookyearEndData"
      :key="by.points"
      :points="by.points"
      class="sf-fill sf-c-snow"
    ></polygon>
    <a
      v-for="ledger in filteredLedgers"
      :key="ledger.ledgerId"
      v-tooltip.html="tooltipContent(ledger)"
      :href="ledgerHref(ledger)"
    >
      <rect
        :x="ledgerCircleX(ledger) - 4"
        fill-opacity="0"
        height="8"
        width="8"
        y="2"
      ></rect>
      <circle
        :class="[
          ledgerIsCurrent(ledger)
            ? 'sf-fill sf-c-primary'
            : 'sf-fill sf-c-text',
        ]"
        :cx="ledgerCircleX(ledger)"
        :data-ledger-id="ledger.id"
        cy="12"
        r="4"
      ></circle>
      <polygon
        :class="[
          ledgerIsCurrent(ledger)
            ? 'sf-fill sf-c-primary'
            : 'sf-fill sf-c-text',
        ]"
        :points="ledgerTrianglePolygon(ledger)"
      ></polygon>
    </a>
  </svg>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import * as d3 from 'd3'

export default {
  props: {
    width: { type: Number, default: 370 },
  },

  data() {
    this.fetchTimelineData()
    return {
      height: 40,
      padding: { top: 5, right: 5, left: 5, bottom: 5 },
    }
  },

  computed: {
    ...mapGetters([
      'timelineData',
      'ledgerId',
      'contextType',
      'contextId',
      'contextHrefParam',
    ]),

    filteredLedgers() {
      return this.timelineData.ledgers.filter(this.showLedger)
    },

    timespanStart() {
      return this.timelineData.timespanStart
    },

    timespanEnd() {
      return this.timelineData.timespanEnd
    },

    // Parallelogram polygon points representing the bookyear
    bookyearEndData() {
      let previousX = 0

      const list = this.timelineData.bookyearEndDates.map((endDate) => {
        const currentX = this.timeScale(endDate)
        const points = this.bookyearParallelogramPolygonPoints(
          previousX,
          currentX
        )
        previousX = currentX
        return { points: points }
      })

      return list
    },

    timeScale() {
      return d3
        .scaleLinear()
        .domain([this.timespanStart, this.timespanEnd])
        .range([this.padding.left, this.width - this.padding.right])
    },

    halfOfMonth() {
      // How many pixels to add for half a month (so we draw the end-of-year lines between 2 months)
      return (
        this.timeScale(
          this.timespanStart.setDate(this.timespanStart.getDate() + 15)
        ) - this.padding.left
      )
    },
  },

  methods: {
    ...mapActions(['fetchTimelineData']),

    tooltipContent(ledger) {
      return `
        <div class='sf-c-snow sf-pad-b-1'>${ledger.ledgerPeriodName}</div>
        <div class="sf-level sf-level-around">
          <div>
            <span class='sf-c-white sf-bold'>${
              ledger.notesCount + ledger.stickyNotesCount
            }</span>
            <span class='sf-icon icon icon-comments'></span>
          </div>
          <div>
            <span class='f-c-white sf-bold'>${
              ledger.todosCount + ledger.stickyTodosCount
            }</span>
            <span class='sf-icon icon icon-bolt ${
              ledger.openTodos ? 'sf-c-danger' : ''
            }'></span>
          </div>
        </div>
      `
    },

    ledgerIsCurrent(ledger) {
      return this.ledgerId === ledger.ledgerId
    },

    showLedger(ledger) {
      // Only show circles for ledgers with at least 1 unsticky note/todo
      return (
        this.ledgerIsCurrent(ledger) ||
        ledger.notesCount + ledger.todosCount > 0
      )
    },

    ledgerCircleX(ledger) {
      return this.timeScale(ledger.ledgerEndDate)
    },

    ledgerTrianglePolygon(ledger) {
      const x = this.ledgerCircleX(ledger)
      return `${x - 4},13 ${x + 4},13 ${x},19`
    },

    bookyearParallelogramPolygonPoints(previousX, currentX) {
      const height = 6
      const y1 = 17
      const y2 = y1 + height
      const x1 = previousX + height / 2 + this.halfOfMonth * 1.5
      const x2 = previousX - height / 2 + this.halfOfMonth * 1.5
      const x3 = currentX - height / 2 + this.halfOfMonth / 2
      const x4 = currentX + height / 2 + this.halfOfMonth / 2
      return `${x1},${y1} ${x2},${y2} ${x3},${y2} ${x4},${y1}`
    },

    ledgerHref(ledger) {
      switch (this.contextType) {
        case 'account':
          return Silverfin.Routes.ledger_account_entry_path({
            firm_id: Silverfin.currentFirmId,
            company_id: Silverfin.currentCompanyId,
            ledger_id: ledger.ledgerId,
            // Adding current_ledger_id to force update the session's ledger so the navbar shows the current period
            current_ledger_id: ledger.ledgerId,
            account_number: this.contextHrefParam,
          })
        case 'reconciliation_text':
          return Silverfin.Routes.ledger_reconciliation_text_path({
            firm_id: Silverfin.currentFirmId,
            company_id: Silverfin.currentCompanyId,
            ledger_id: ledger.ledgerId,
            current_ledger_id: ledger.ledgerId,
            id: this.contextId,
            // Tell the routes generator that all params are passed in this options hash
            _options: true,
          })
        case 'report_template':
          return Silverfin.Routes.report_path({
            firm_id: Silverfin.currentFirmId,
            company_id: Silverfin.currentCompanyId,
            id: this.contextId,
            current_ledger_id: ledger.ledgerId,
            _options: true,
          })
        case 'company':
          return Silverfin.Routes.ledger_company_workflows_path({
            firm_id: Silverfin.currentFirmId,
            company_id: Silverfin.currentCompanyId,
            ledger_id: ledger.ledgerId,
            _options: true,
          })
        case 'budget_set':
          return Silverfin.Routes.budget_set_path({
            firm_id: Silverfin.currentFirmId,
            company_id: Silverfin.currentCompanyId,
            id: this.contextId,
            _options: true,
          })
      }
    },
  },
}
</script>
